/**
 * ForgotPasswordForm - Forgot password form with modern Dashboard3 styling
 */

"use client";

import { Box, Flex } from "@chakra-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Button, Heading, Text } from "@/components/ui";
import { LabeledInput } from "@/components/shared/formElements/LabeledInput";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useAuthForgotPassword, useTenantData } from "@/hooks";
import { type ForgotPasswordFormSchema, forgotPasswordSchema } from "@/schemas";
import NextLink from "next/link";
import { PageTypePortal } from "@/lib/types";
import { useToast } from "@/components/feedback";
import { Link } from "@/components/ui";
import { useTenantContentSettingsGet } from "@/features/profile-settings/hooks/useTenantContentSettingsGet";
import { ContentSettingsText } from "@/lib/utils/content-settings/ContentSettingsText";
import { useHeroVisibility } from "@/lib/contexts";
import { HiOutlineChevronLeft } from "react-icons/hi2";

export type ForgotPasswordFormProps = {
  resetPasswordPath?: string;
  backToSignInPath?: string;
};

export const ForgotPasswordForm = ({
  resetPasswordPath,
  backToSignInPath,
}: ForgotPasswordFormProps = {}) => {
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const toast = useToast();
  const theme = useThemeTokens();
  const { hideHero } = useHeroVisibility();
  const { tenantId, tenantData } = useTenantData();
  const { data: contentSettings } = useTenantContentSettingsGet(
    { tenantId },
    { enabled: !!tenantId },
  );

  const clientBaseUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const path = resetPasswordPath ?? PageTypePortal.ResetPassword;
    return `${window.location.origin}${path}`;
  }, [resetPasswordPath]);

  const form = useForm<ForgotPasswordFormSchema>({
    resolver: zodResolver(forgotPasswordSchema),
    mode: "onChange",
  });

  const { register, handleSubmit, formState } = form;
  const { errors, isValid } = formState;

  const { mutate, isPending, isSuccess, isError } = useAuthForgotPassword(toast);

  const handleResend = useCallback(() => {
    mutate({ email: submittedEmail, clientBaseUrl });
  }, [mutate, submittedEmail, clientBaseUrl]);

  const onSubmit = useCallback(
    (data: ForgotPasswordFormSchema) => {
      setSubmittedEmail(data.email);
      setHasSubmitted(true);
      mutate({ email: data.email, clientBaseUrl });
    },
    [mutate, clientBaseUrl],
  );

  const showSuccessView = useMemo(
    () => hasSubmitted && isSuccess && !isError,
    [hasSubmitted, isSuccess, isError],
  );

  useEffect(() => {
    if (showSuccessView) {
      hideHero();
    }
  }, [showSuccessView, hideHero]);

  if (showSuccessView) {
    return (
      <Flex direction="column" w="100%" gap={6}>
        <Box>
          <Text
            fontSize="xs"
            textTransform="uppercase"
            letterSpacing="0.18em"
            color={theme.text.tertiary}
            mb={2}
          >
            {contentSettings.forgotPasswordSuccessLabel}
          </Text>
          <Heading size="xl" fontWeight="600" letterSpacing="-0.03em" mb={3}>
            {contentSettings.forgotPasswordSuccessTitle}
          </Heading>
          <ContentSettingsText
            variant="secondary"
            fontSize="sm"
            lineHeight="1.6"
            tenantEmail={tenantData?.email}
            userEmail={submittedEmail}
            data-testid="forgot-password-success-body"
          >
            {contentSettings.forgotPasswordSuccessDescription}
          </ContentSettingsText>
        </Box>

        <Button
          h="44px"
          w="100%"
          onClick={handleResend}
          fontSize="sm"
          variant="primary"
          fontWeight="500"
          isLoading={isPending}
          isDisabled={isPending}
          loadingText="Sending..."
        >
          {contentSettings.forgotPasswordResendButtonText}
        </Button>
      </Flex>
    );
  }

  return (
    <Flex direction="column" w="100%" gap={6}>
      {backToSignInPath ? (
        <Flex justify="flex-start">
          <Link
            display="inline-flex"
            alignItems="center"
            gap={1.5}
            as={NextLink}
            href={backToSignInPath}
            fontSize="sm"
            fontWeight="500"
            sx={{
              "@media (hover: hover)": {
                "&:hover": { textDecoration: "underline" },
              },
            }}
          >
            <Box as={HiOutlineChevronLeft} display="flex" alignItems="center" flexShrink={0} />
            Back to sign in
          </Link>
        </Flex>
      ) : null}

      <form onSubmit={handleSubmit(onSubmit)} style={{ width: "100%" }}>
        <Flex direction="column" gap={4}>
          <LabeledInput
            name="email"
            label="Email"
            errors={errors}
            register={register}
            type="email"
            placeholder="mail@example.com"
            required
          />

          <Button
            h="44px"
            w="100%"
            type="submit"
            fontSize="sm"
            variant="primary"
            fontWeight="500"
            isLoading={isPending}
            loadingText="Sending..."
            isDisabled={!isValid || isPending}
          >
            Send Reset Link
          </Button>
        </Flex>
      </form>
    </Flex>
  );
};
